/**
 * Supplier List Page
 *
 * Displays all suppliers with halal certification tracking:
 * - Supplier list with certification status
 * - Certificate expiry warnings
 * - Filter by supplier type and certification status
 * - Quick actions (view, add supplier)
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Users, Plus, Filter } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { Table } from '../../components/common/Table';
import { Select } from '../../components/forms/Select';
import { Alert } from '../../components/common/Alert';
import { EmptyState } from '../../components/common/EmptyState';
import { TableSkeleton } from '../../components/common/Skeleton';
import { mockApi } from '../../services/mockApi';
import { formatDate } from '../../utils/formatting';
import type { Supplier } from '../../types/supplier';

export const SupplierListPage: React.FC = () => {
  const { t } = useTranslation(['common', 'company']);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState<Supplier[]>([]);
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [certificationFilter, setCertificationFilter] = useState<string>('ALL');

  useEffect(() => {
    loadSuppliers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [suppliers, typeFilter, certificationFilter]);

  const loadSuppliers = async () => {
    setLoading(true);
    try {
      const response = await mockApi.getAllSuppliers();
      if (response.success && response.data) {
        setSuppliers(response.data);
      }
    } catch (error) {
      console.error('Error loading suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...suppliers];

    if (typeFilter !== 'ALL') {
      filtered = filtered.filter((s) => s.type === typeFilter);
    }

    if (certificationFilter === 'CERTIFIED') {
      filtered = filtered.filter((s) => s.halalCertified);
    } else if (certificationFilter === 'NOT_CERTIFIED') {
      filtered = filtered.filter((s) => !s.halalCertified);
    } else if (certificationFilter === 'EXPIRING') {
      filtered = filtered.filter((s) => isExpiringSoon(s));
    }

    setFilteredSuppliers(filtered);
  };

  const isExpiringSoon = (supplier: Supplier): boolean => {
    if (!supplier.halalCertified || !supplier.certificationExpiryDate) return false;
    const daysUntilExpiry = Math.ceil(
      (new Date(supplier.certificationExpiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
    return daysUntilExpiry <= 60 && daysUntilExpiry > 0;
  };

  const typeOptions = [
    { value: 'ALL', label: 'All Types' },
    { value: 'INGREDIENTS', label: t('company:suppliers.types.ingredients') },
    { value: 'PACKAGING', label: t('company:suppliers.types.packaging') },
    { value: 'EQUIPMENT', label: t('company:suppliers.types.equipment') },
    { value: 'SERVICES', label: t('company:suppliers.types.services') },
  ];

  const certificationOptions = [
    { value: 'ALL', label: 'All Certifications' },
    { value: 'CERTIFIED', label: 'Halal Certified' },
    { value: 'NOT_CERTIFIED', label: 'Not Certified' },
    { value: 'EXPIRING', label: 'Expiring Soon' },
  ];

  const expiringCount = suppliers.filter(isExpiringSoon).length;
  const certifiedCount = suppliers.filter((s) => s.halalCertified).length;

  return (
    <PageLayout
      pageTitle={t('company:suppliers.title')}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('company:dashboard.title'), href: '/company/dashboard' },
        { label: t('company:suppliers.title') },
      ]}
      actions={
        <Button variant="primary" onClick={() => navigate('/company/suppliers/add')}>
          + {t('company:suppliers.addSupplier')}
        </Button>
      }
    >
      {/* Expiring certificates warning */}
      {expiringCount > 0 && (
        <Alert type="warning" title="Certificates Expiring Soon" dismissible className="mb-6">
          {expiringCount} supplier certificate{expiringCount > 1 ? 's' : ''} will expire within 60 days.
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardBody>
            <div className="text-sm text-gray-600">Total Suppliers</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">{suppliers.length}</div>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <div className="text-sm text-gray-600">Halal Certified</div>
            <div className="text-2xl font-bold text-green-600 mt-1">{certifiedCount}</div>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <div className="text-sm text-gray-600">Expiring Soon</div>
            <div className="text-2xl font-bold text-orange-600 mt-1">{expiringCount}</div>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Supplier Type"
              value={typeFilter}
              options={typeOptions}
              onChange={(e) => setTypeFilter(e.target.value)}
            />

            <Select
              label="Certification Status"
              value={certificationFilter}
              options={certificationOptions}
              onChange={(e) => setCertificationFilter(e.target.value)}
            />

            <div className="flex items-end">
              <div className="text-sm text-gray-600">
                Showing {filteredSuppliers.length} of {suppliers.length} suppliers
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Suppliers Table */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Suppliers</h3>
        </CardHeader>
        <CardBody>
          {loading ? (
            <TableSkeleton rows={5} />
          ) : filteredSuppliers.length === 0 ? (
            suppliers.length === 0 ? (
              <EmptyState
                icon={<Users className="w-10 h-10" />}
                title="No Suppliers Yet"
                description="Start building your supplier network by adding your first halal-certified supplier. Track certifications and ensure compliance across your supply chain."
                action={{
                  label: 'Add First Supplier',
                  onClick: () => navigate('/company/suppliers/add'),
                  icon: <Plus className="w-5 h-5 mr-2" />,
                }}
              />
            ) : (
              <EmptyState
                icon={<Filter className="w-10 h-10" />}
                title="No Matching Suppliers"
                description="No suppliers match your current filter criteria. Try adjusting the filters or clearing them to see all suppliers."
                action={{
                  label: 'Clear All Filters',
                  onClick: () => {
                    setTypeFilter('ALL');
                    setCertificationFilter('ALL');
                  },
                }}
              />
            )
          ) : (
            <Table
              columns={[
                {
                  key: 'name',
                  header: t('company:suppliers.supplierName'),
                  render: (supplier: Supplier) => (
                    <div>
                      <div className="font-medium text-blue-600 hover:underline cursor-pointer">
                        {supplier.name}
                      </div>
                      <div className="text-xs text-gray-500">{supplier.type.replace(/_/g, ' ')}</div>
                    </div>
                  ),
                },
                {
                  key: 'contact',
                  header: 'Contact',
                  render: (supplier: Supplier) => (
                    <div className="text-sm">
                      <div>{supplier.contactPerson.name}</div>
                      <div className="text-xs text-gray-500">{supplier.contactPerson.email}</div>
                    </div>
                  ),
                },
                {
                  key: 'certification',
                  header: t('company:suppliers.halalCertified'),
                  render: (supplier: Supplier) => (
                    <div>
                      {supplier.halalCertified ? (
                        <>
                          <Badge variant="success" size="sm">
                            Certified
                          </Badge>
                          {isExpiringSoon(supplier) && (
                            <div className="mt-1 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3 text-warning-600" />
                              <Badge variant="warning" size="sm">
                                Expiring Soon
                              </Badge>
                            </div>
                          )}
                        </>
                      ) : (
                        <Badge variant="default" size="sm">
                          Not Certified
                        </Badge>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'certificationNumber',
                  header: t('company:suppliers.certificationNumber'),
                  render: (supplier: Supplier) => (
                    <div className="text-sm">
                      {supplier.certificationNumber || <span className="text-gray-400">N/A</span>}
                    </div>
                  ),
                },
                {
                  key: 'expiryDate',
                  header: t('company:suppliers.certificationExpiry'),
                  render: (supplier: Supplier) => (
                    <div className="text-sm">
                      {supplier.certificationExpiryDate ? (
                        <div className={isExpiringSoon(supplier) ? 'text-orange-600 font-medium' : ''}>
                          {formatDate(supplier.certificationExpiryDate)}
                          {isExpiringSoon(supplier) && (
                            <div className="text-xs">
                              ({Math.ceil((new Date(supplier.certificationExpiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} days)
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'actions',
                  header: 'Actions',
                  render: (supplier: Supplier) => (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/company/suppliers/${supplier.id}`)}
                      >
                        {t('common:actions.view')}
                      </Button>
                    </div>
                  ),
                },
              ]}
              data={filteredSuppliers}
              keyExtractor={(supplier) => supplier.id}
              onRowClick={(supplier) => navigate(`/company/suppliers/${supplier.id}`)}
            />
          )}
        </CardBody>
      </Card>
    </PageLayout>
  );
};

export default SupplierListPage;

